import { AxiosError, type AxiosRequestConfig, type AxiosResponse } from "axios"

export const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined

export function requestPath(config?: Pick<AxiosRequestConfig, "url">): string {
  const url = config?.url || ""
  const path = /^https?:\/\//i.test(url) ? new URL(url).pathname : url.split(/[?#]/)[0]
  return path
    .replace(/^\/+/, "/")
    .replace(/^\/api(?=\/v1\/)/, "")
    .replace(/\/+$/, "")
}

const credentialPaths = new Set([
  "/v1/auth/register",
  "/v1/auth/login",
  "/v1/auth/verify",
  "/v1/auth/google",
  "/v1/auth/admin-login",
  "/v1/auth/admin-reset-password",
  "/v1/auth/admin-reset-password/confirm",
  "/v1/auth/admin-invites/accept",
  "/v1/auth/rotate-tokens",
  "/v1/owner/auth/signup",
  "/v1/owner/auth/login",
  "/v1/owner/auth/verify",
  "/v1/owner/auth/password-login",
  "/v1/owner/auth/google",
  "/v1/owner/auth/forgot-password",
  "/v1/owner/auth/reset-password",
  "/v1/owner/auth/accept-invite",
  "/v1/owner/auth/refresh-token",
])

export function isCredentialRequest(config?: Pick<AxiosRequestConfig, "url" | "method">): boolean {
  return config?.method?.toUpperCase() === "POST" && credentialPaths.has(requestPath(config))
}

export const isCancelledRequest = (error: unknown): boolean =>
  asRecord(error)?.code === "ERR_CANCELED" || asRecord(error)?.__CANCEL__ === true

export function apiErrorDetails(error: unknown) {
  const err = error as AxiosError | undefined
  const body = asRecord(err?.response?.data)
  const legacyCode = typeof body?.error === "string" && /^[A-Z][A-Z0-9_]+$/.test(body.error) ? body.error : undefined
  const requestId = err?.response?.headers?.["x-request-id"] ?? body?.requestId
  return {
    status: err?.response?.status,
    code:
      typeof asRecord(error)?.apiCode === "string"
        ? (asRecord(error)!.apiCode as string)
        : typeof body?.code === "string"
          ? body.code
          : legacyCode,
    requestId: typeof requestId === "string" ? requestId : undefined,
    message:
      typeof body?.message === "string"
        ? body.message
        : typeof body?.error === "string"
          ? body.error
          : err?.message || "An unexpected error occurred.",
  }
}

/** Transitional bare-403 match is only for Hub/Admin's existing session guards. */
export function isSessionFailure(error: unknown, allowLegacyForbidden = false): boolean {
  const err = error as AxiosError | undefined
  if (isCancelledRequest(error) || isCredentialRequest(err?.config) || !requestPath(err?.config).startsWith("/v1/"))
    return false
  return (
    err?.response?.status === 401 ||
    (allowLegacyForbidden && err?.response?.status === 403 && err.response.data === "Forbidden")
  )
}

export class ApiResponseError extends AxiosError {
  readonly isApiResponseError = true
  readonly requestId?: string
  constructor(
    message: string,
    response: AxiosResponse,
    readonly apiCode = "INVALID_API_RESPONSE",
  ) {
    super(message, AxiosError.ERR_BAD_RESPONSE, response.config, response.request, response)
    this.name = "ApiResponseError"
    this.requestId = apiErrorDetails(this).requestId
  }
}

/** Opt in only after auditing the endpoint's resolved failure callers. */
export function requireSuccessfulResponse<T>(response: AxiosResponse<T>): AxiosResponse<T> {
  if (asRecord(response.data)?.success === false) {
    const details = apiErrorDetails({ response } as AxiosError)
    throw new ApiResponseError(details.message, response, details.code || "API_RESPONSE_FAILURE")
  }
  return response
}

export const isResponseFailure = (error: unknown) => asRecord(error)?.isApiResponseError === true

export interface PageMetadata {
  page: number
  limit: number
  totalCount: number
  totalPages: number
}
const integer = (value: unknown, min: number): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= min

export function requirePageEnvelope<T>(
  response: AxiosResponse,
  collection: string,
  validRow: (row: unknown) => row is T,
): { rows: T[]; pagination: PageMetadata } {
  requireSuccessfulResponse(response)
  const body = asRecord(response.data)
  const rows = body?.[collection]
  const page = asRecord(body?.pagination)
  if (
    body?.success !== true ||
    !Array.isArray(rows) ||
    !rows.every(validRow) ||
    !page ||
    !integer(page.page, 1) ||
    !integer(page.limit, 1) ||
    !integer(page.totalCount, 0) ||
    !integer(page.totalPages, 0) ||
    page.totalPages !== Math.ceil(page.totalCount / page.limit) ||
    rows.length > page.limit
  ) {
    throw new ApiResponseError("The server returned an invalid page. Please retry.", response)
  }
  return { rows, pagination: page as unknown as PageMetadata }
}
